import { apiRequest } from '@/services/axios.ts'

const PREVIEWABLE_EXTENSIONS = new Set([
    'gif',
    'jpeg',
    'jpg',
    'pdf',
    'png'
])

function canPreviewInBrowser(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? ''

    return PREVIEWABLE_EXTENSIONS.has(extension)
}

function getFileNameFromDisposition(contentDisposition?: string, fallbackFileName = 'download') {
    if (!contentDisposition) {
        return fallbackFileName
    }

    const utf8Name = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    if (utf8Name) {
        return decodeURIComponent(utf8Name)
    }

    const plainName = contentDisposition.match(/filename="?([^"]+)"?/i)?.[1]

    return plainName || fallbackFileName
}

async function openProtectedFile(url: string, fallbackFileName: string) {
    const shouldPreview = canPreviewInBrowser(fallbackFileName)
    const previewWindow = shouldPreview ? window.open('', '_blank') : null

    try {
        const response = await apiRequest(
            'get',
            url,
            {},
            {
                responseType: 'blob'
            }
        )

        const contentType = response.headers['content-type'] || 'application/octet-stream'
        const blob = new Blob([response.data], {
            type: contentType
        })
        const objectUrl = window.URL.createObjectURL(blob)

        if (shouldPreview) {
            if (previewWindow) {
                previewWindow.location.href = objectUrl
            } else {
                const previewLink = document.createElement('a')
                previewLink.href = objectUrl
                previewLink.target = '_blank'
                document.body.appendChild(previewLink)
                previewLink.click()
                previewLink.remove()
            }

            window.setTimeout(() => {
                window.URL.revokeObjectURL(objectUrl)
            }, 60000)

            return
        }

        const fileName = getFileNameFromDisposition(
            response.headers['content-disposition'],
            fallbackFileName
        )
        const link = document.createElement('a')
        link.href = objectUrl
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
        if (previewWindow) {
            previewWindow.close()
        }

        throw error
    }
}

export { openProtectedFile }
